import { useLeaderboard, type Ranked } from "./leaderboard";
import { useT } from "../settings/context";

/**
 * ═══ LE CLASSEMENT, ET SON PODIUM ═══
 *
 * ⚠️ C'EST UN TYPE D'ACTIVITÉ, PAS UN MODE D'AFFICHAGE. Il était commandé par un
 * sélecteur « ce que la salle affiche », retiré au tri du 2026-08-12 : la route
 * existait, testée, et plus rien ne pouvait l'afficher. En faire une activité lui rend
 * les gestes de toutes les autres — on le prépare, on le LANCE, il apparaît en salle et
 * sur les téléphones — au lieu d'ajouter un second mécanisme à côté du premier.
 *
 * ⚠️ IL SE RELIT, IL N'EST PAS POUSSÉ (voir `leaderboard.ts`) : il change à chaque
 * bonne réponse de chaque personne, donc le diffuser serait un message par vote pour
 * une donnée que personne ne regarde tant qu'elle n'est pas à l'écran. Il n'est demandé
 * que quand il EST à l'écran, et rafraîchi tant qu'il y reste.
 *
 * ⚠️ TROIS D'ABORD, LA SUITE ENSUITE. Le podium ne se lit pas comme une liste : c'est
 * la hauteur des marches qui dit le rang, à vingt mètres, avant même qu'on lise un nom.
 * Les suivants prennent la liste, qui elle se lit de près.
 *
 * ⚠️ ET IL NE PORTE QUE CE QU'UN BADGE PORTE DÉJÀ — prénom, nom, ou le pseudonyme
 * quand il y en a un. Jamais d'adresse e-mail : c'est le serveur qui le garantit
 * (`routes/live/leaderboard.js`), et cet écran s'affiche sur dix mètres de large.
 */
/**
 * ⚠️ MOINS DE MONDE SUR LE MUR QUE SUR UN TÉLÉPHONE, et ce n'est pas une préférence :
 * un mur ne défile pas. Dix classés faisaient sortir la liste par le bas de l'écran et
 * le titre par le haut, mesuré en 1920x1080. Le téléphone, lui, défile : il en garde
 * dix. Le podium en prend trois, la liste montre la suite.
 */
const HOW_MANY = { room: 8, phone: 10 };

export function Podium({
  room = false,
  screenToken,
  top,
}: {
  room?: boolean;
  /** La salle lit avec son jeton d'écran, comme pour le reste du direct. */
  screenToken?: string | null;
  top?: number;
}) {
  const t = useT();
  const { data, isPending, isError } = useLeaderboard({
    enabled: true,
    screenToken,
    top: top ?? (room ? HOW_MANY.room : HOW_MANY.phone),
  });

  if (isPending) {
    return (
      <p className="empty">{t("LIVE_RANKING_LOADING", "Chargement du classement.")}</p>
    );
  }

  /* Dire que la lecture a raté plutôt que d'afficher un classement vide : les deux se
     ressemblent, et l'un des deux est un mensonge. */
  if (isError) {
    return (
      <p className="empty">
        {t("LIVE_RANKING_UNREACHABLE", "Le classement n'a pas pu être chargé.")}
      </p>
    );
  }

  /**
   * ⚠️ ZÉRO POINT N'EST PAS UN RANG. Le serveur rend les N premiers par points, et sur
   * un événement où top personnes ont répondu, les sept suivantes arrivent à zéro :
   * le mur affichait « 7e, 0 point », « 8e, 0 point », nommément, sur dix mètres. Un
   * classement dit qui a marqué ; ceux qui n'ont pas encore joué n'ont pas à être
   * nommés devant la salle.
   */
  const ranked = (data ?? []).filter((row) => row.points > 0);

  if (!ranked.length) {
    return (
      <p className="empty">
        {t("LIVE_RANKING_EMPTY", "Personne n'a encore marqué de point.")}
      </p>
    );
  }

  const steps = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  return (
    <>
      {/* L'ordre visuel du podium n'est pas l'ordre du classement : le premier est au
          MILIEU, entre le deuxième et le troisième. C'est ce qu'on reconnaît sans
          lire, et c'est le seul endroit où l'ordre d'affichage diverge des données. */}
      <div className="podium">
        {podiumOrder(steps).map((row) =>
          row ? (
            /* ⚠️ `data-rang`, LE MOT DE LA FEUILLE ET DE LA TRAPPE CSS. Traduit en
               `data-row` au passage du code en anglais, il ne visait plus rien : les
               trois marches avaient la même hauteur, et le rang ne se lisait plus. */
            <div className="podium__c" key={row.id} data-rang={row.rank}>
              <span className="podium__n">{row.name}</span>
              <span className="podium__b">{row.rank}</span>
              <span className="podium__p">
                {row.points} {row.points > 1 ? t("POINTS", "points") : t("POINT", "point")}
              </span>
            </div>
          ) : null,
        )}
      </div>

      {rest.length ? (
        <div className="ld">
          {rest.map((row) => (
            <div className="ld__r" key={row.id}>
              <i>{row.rank}</i>
              <span>{row.name}</span>
              <b>
                {row.points} {row.points > 1 ? t("POINTS", "points") : t("POINT", "point")}
              </b>
            </div>
          ))}
        </div>
      ) : null}

      {/* La salle n'a personne à qui s'adresser ; sur un téléphone, dire d'où viennent
          les points évite qu'on cherche un vote qu'on aurait raté. */}
      {!room ? (
        <small className="hint">
          {t(
            "LIVE_RANKING_SOURCE",
            "Les points viennent des bonnes réponses aux quiz.",
          )}
        </small>
      ) : null}
    </>
  );
}

/**
 * LE PREMIER AU MILIEU, le deuxième à sa gauche, le troisième à sa droite.
 *
 * Rendue à part pour être vérifiable : c'est la seule règle de ce composant, et elle
 * doit tenir avec un, deux ou top classés — un événement dont deux personnes ont
 * répondu ne doit pas afficher un trou au milieu.
 */
export function podiumOrder(
  top: ReadonlyArray<Ranked>,
): Array<Ranked | null> {
  const [first, second, third] = top;
  return [second ?? null, first ?? null, third ?? null].filter(
    (slot, index, all) =>
      /* Une place vide n'est gardée que si quelqu'un occupe une place APRÈS elle :
         sinon le podium à deux laisserait une colonne fantôme à droite. */
      slot !== null || all.slice(index + 1).some((other) => other !== null),
  );
}
